import type { PaginationState, Table as TableInstance } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback, useEffect } from 'react';
import type { DataGridAnalyticsEvent, DataGridPaginationState } from './types';

interface UseDataGridPaginationArgs<TData> {
  /** The TanStack Table instance. Used to read filtered row count and current pagination state. */
  table: TableInstance<TData>;
  /** The current controlled pagination state (`{ pageIndex, pageSize }`). */
  pagination: PaginationState;
  /** React state setter for pagination. Updates are picked up by the table via controlled state. */
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  /** Whether the DataGrid is using server-side data fetching. */
  isServerSide: boolean;
  /** Whether data is currently loading. Prevents bounds-checking from rejecting valid navigations during fetch. */
  isLoading: boolean;
  /** Total row count from the server. Required for server-side pagination to compute page count. */
  totalRowCount?: number;
  /** Server-side callback fired when pagination changes. Typically triggers a data refetch. */
  onPaginationChange?: (pagination: DataGridPaginationState) => void;
  /** External pagination state to sync from (e.g., URL params). Used for server-side back/forward navigation. */
  initialPagination?: DataGridPaginationState;
  /** Analytics callback. Fired on page navigation so clicks bypass handlePaginationChange in DataGrid. */
  onAnalyticsEvent?: (event: DataGridAnalyticsEvent) => void;
}

interface UseDataGridPaginationReturn {
  /** Current 1-indexed page number. */
  currentPage: number;
  /** Total number of pages based on row count and page size. */
  totalPages: number;
  /** 1-indexed row number of the first visible row. 0 when empty. */
  startRow: number;
  /** 1-indexed row number of the last visible row. 0 when empty. */
  endRow: number;
  /** Total row count: `totalRowCount` for server-side, filtered row count for client-side. */
  currentTotalRows: number;
  /** Navigate to a specific page (1-indexed). */
  handlePageChange: (page: number) => void;
  /** Change the page size. Resets to page 1. Value is a string from the page size select. */
  handlePageSizeChange: (value: string) => void;
  /** Returns the page numbers and ellipsis markers for rendering pagination buttons. */
  getVisiblePages: () => (number | 'ellipsis')[];
}

/**
 * Manages DataGrid pagination state, derived values (page count, row range), and page navigation.
 *
 * Updates pagination via `setPagination()` only — not via `table.setPageIndex()`/`table.setPageSize()`.
 * The table uses controlled state (`state.pagination`) and picks up changes on re-render. This avoids
 * double state updates that occurred when both table setters and direct state updates were used.
 *
 * For server-side mode, also calls `onPaginationChange` to trigger data refetches, and includes
 * effects to sync from external state (`initialPagination`) and clamp the page index when the
 * total row count changes.
 */
export function useDataGridPagination<TData>({
  table,
  pagination,
  setPagination,
  isServerSide,
  isLoading,
  totalRowCount,
  onPaginationChange,
  initialPagination,
  onAnalyticsEvent,
}: UseDataGridPaginationArgs<TData>): UseDataGridPaginationReturn {
  const currentTotalRows = isServerSide && totalRowCount !== undefined ? totalRowCount : table.getFilteredRowModel().rows.length;

  const { pageIndex, pageSize: currentPageSize } = table.getState().pagination;

  const currentPage = pageIndex + 1;
  const totalPages = Math.ceil(currentTotalRows / currentPageSize);

  const validPageIndex = currentTotalRows === 0 ? 0 : Math.min(pageIndex, Math.max(0, totalPages - 1));
  const startRow = currentTotalRows === 0 ? 0 : validPageIndex * currentPageSize + 1;
  const endRow = currentTotalRows === 0 ? 0 : Math.min(startRow + currentPageSize - 1, currentTotalRows);

  // Note: These handlers update pagination via setPagination() only — not via table.setPageIndex()/
  // table.setPageSize(). The table uses controlled state (state.pagination) and picks up changes on
  // re-render. Calling table setters would also work but triggers handlePaginationChange in
  // DataGrid.tsx synchronously, which caused double setPagination/onPaginationChange calls.

  const handlePageChange = useCallback(
    (page: number) => {
      const newPagination = { pageIndex: page - 1, pageSize: currentPageSize };
      setPagination(newPagination);
      onAnalyticsEvent?.({ type: 'paginated', page, pageSize: currentPageSize });

      if (isServerSide) {
        onPaginationChange?.(newPagination);
      }
    },
    [currentPageSize, setPagination, isServerSide, onPaginationChange, onAnalyticsEvent],
  );

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const newPagination = { pageIndex: 0, pageSize: Number(value) };
      setPagination(newPagination);
      onAnalyticsEvent?.({ type: 'paginated', page: 1, pageSize: Number(value) });

      if (isServerSide) {
        onPaginationChange?.(newPagination);
      }
    },
    [setPagination, isServerSide, onPaginationChange, onAnalyticsEvent],
  );

  const getVisiblePages = useCallback(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1];

    if (currentPage <= 4) {
      pages.push(2, 3, 4, 5, 'ellipsis', totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push('ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push('ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
    }

    return pages;
  }, [totalPages, currentPage]);

  // Sync external state changes for server-side scenarios
  useEffect(() => {
    if (isServerSide && initialPagination) {
      setPagination(initialPagination);
    }
  }, [isServerSide, initialPagination?.pageIndex, initialPagination?.pageSize, setPagination]);

  // Reset page index if it exceeds available pages
  useEffect(() => {
    // Only validate when not loading to prevent rejecting valid navigations during transitional states
    if (isServerSide && !isLoading && totalRowCount !== undefined) {
      const maxPageIndex = Math.max(0, Math.ceil(totalRowCount / pagination.pageSize) - 1);
      if (pagination.pageIndex > maxPageIndex) {
        const newPagination = { ...pagination, pageIndex: Math.max(0, maxPageIndex) };
        setPagination(newPagination);
        onPaginationChange?.(newPagination);
      }
    }
  }, [isServerSide, isLoading, totalRowCount, pagination, onPaginationChange, setPagination]);

  return {
    currentPage,
    totalPages,
    startRow,
    endRow,
    currentTotalRows,
    handlePageChange,
    handlePageSizeChange,
    getVisiblePages,
  };
}
